/**
 * Pure helpers for the Inventory page (tracker item 30): /inventory with the
 * valuation list, Receive stock, Adjust count and the per-item history.
 *
 * Quantities and money are handled in integer hundredths (utils/cents) so a
 * preview never sums floats. It is display arithmetic only: the server
 * validates every change against the locked on-hand and posts the GL entry.
 */

import * as types from '../api/types';
import { getErrorCode, getErrorMessage } from '../utils/apiError';
import { centsToDecimal, decimalToCents, parseAmountCents } from '../utils/cents';
import { invoiceDate, invoiceMoney } from './invoiceUi';
import { INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE, ledgerEntryPath } from './ledgerUi';

export const INVENTORY_PATH = '/inventory';
export const INVENTORY_HISTORY_PAGE_SIZE = 20;
/** Backend limit on a receipt memo and an adjustment reason. */
export const INVENTORY_TEXT_MAX_LENGTH = 500;
/** GL 1300 Inventory - Materials: never an offset account. */
export const INVENTORY_ACCOUNT_NUMBER = '1300';
/** Default offset account for a receipt (2000 Accounts Payable). */
export const DEFAULT_OFFSET_ACCOUNT_NUMBER = '2000';

/** Query-string value of the "Low stock only" filter (`?lowStock=1`). */
export const LOW_STOCK_PARAM = 'lowStock';

/** /inventory with the search, the low-stock filter and the open history item in the query string (blank values left out). */
export function inventoryPath(params: { q?: string; itemId?: string; lowStock?: boolean } = {}): string {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.lowStock) search.set(LOW_STOCK_PARAM, '1');
  if (params.itemId) search.set('itemId', params.itemId);
  const query = search.toString();
  return query ? `${INVENTORY_PATH}?${query}` : INVENTORY_PATH;
}

// ============ Formatting ============

function groupDigits(whole: number): string {
  return whole.toLocaleString('en-US');
}

/**
 * A quantity for display: thousands separators, and 2 decimals only when the
 * quantity is fractional ("8.00" -> "8", "1234.50" -> "1,234.50").
 */
export function formatQuantity(value: string | null | undefined): string {
  const hundredths = decimalToCents(value);
  if (hundredths === null) return value ?? '—';
  const sign = hundredths < 0 ? '-' : '';
  const abs = Math.abs(hundredths);
  const fraction = abs % 100;
  return `${sign}${groupDigits(Math.floor(abs / 100))}${fraction ? `.${String(fraction).padStart(2, '0')}` : ''}`;
}

/** A signed quantity change ("+10", "-2.50", "0"). */
export function formatQuantityChange(value: string | null | undefined): string {
  const hundredths = decimalToCents(value);
  const text = formatQuantity(value);
  return hundredths !== null && hundredths > 0 ? `+${text}` : text;
}

/** Hundredths as a quantity for display. */
export const quantityFromHundredths = (hundredths: number): string => formatQuantity(centsToDecimal(hundredths));

/** Money for a decimal string, or an em dash when there is none. */
export function inventoryMoney(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  return invoiceMoney(value);
}

/** Money for integer cents. */
export const inventoryMoneyFromCents = (cents: number): string => invoiceMoney(centsToDecimal(cents));

export const inventoryDate = (value: string | null | undefined): string => invoiceDate(value);

// ============ Valuation ============

/**
 * Valuation rows whose name contains `query` (case-insensitive), and only the
 * low-stock ones (server-computed `lowStock`) when `lowStockOnly`.
 */
export function filterValuationItems(
  items: types.InventoryValuationItem[],
  query: string,
  lowStockOnly = false
): types.InventoryValuationItem[] {
  const needle = query.trim().toLowerCase();
  return items.filter((item) => (!lowStockOnly || !!item.lowStock) && (!needle || item.name.toLowerCase().includes(needle)));
}

/** Low-stock rows in the valuation: the server's lowStockCount, else counted from the rows. */
export function lowStockCount(valuation: types.InventoryValuation): number {
  return valuation.lowStockCount ?? valuation.items.filter((item) => item.lowStock).length;
}

/** Tooltip of the "Low stock" badge. */
export function lowStockTitle(item: Pick<types.InventoryValuationItem, 'reorderPoint'>): string {
  return `On hand is at or below the reorder point (${formatQuantity(item.reorderPoint ?? null)})`;
}

export interface ValuationComparison {
  totalCents: number;
  /** GL 1300 balance; null when the tenant has no 1300 account */
  glBalanceCents: number | null;
  /** totalValue - GL balance, from the server (signed) */
  differenceCents: number;
  reconciled: boolean;
}

export function valuationComparison(valuation: types.InventoryValuation): ValuationComparison {
  const differenceCents = decimalToCents(valuation.difference) ?? 0;
  return {
    totalCents: decimalToCents(valuation.totalValue) ?? 0,
    glBalanceCents: valuation.glAccount ? decimalToCents(valuation.glAccount.balance) ?? 0 : null,
    differenceCents,
    reconciled: differenceCents === 0,
  };
}

/** Signed money for the difference ("+$12.00", "-$3.50", "$0.00"). */
export function signedMoney(cents: number): string {
  const text = inventoryMoneyFromCents(Math.abs(cents));
  if (cents > 0) return `+${text}`;
  if (cents < 0) return `-${text}`;
  return text;
}

/** One sentence explaining the difference between the stock value and GL 1300 (always shown). */
export function differenceExplanation(valuation: types.InventoryValuation): string {
  const { differenceCents, reconciled } = valuationComparison(valuation);
  if (!valuation.glAccount) {
    return 'The chart of accounts has no 1300 Inventory - Materials account, so its balance is taken as $0.00. Add the account before receiving or adjusting stock.';
  }
  if (reconciled) return 'The stock value matches the GL 1300 balance.';
  const side = differenceCents > 0 ? 'higher' : 'lower';
  return (
    `The stock value is ${inventoryMoneyFromCents(Math.abs(differenceCents))} ${side} than the GL 1300 balance. ` +
    'This happens when stock was valued at a cost other than the item cost, an item cost changed while stock was on hand, ' +
    'a product without an expense account was invoiced, or stock predates inventory postings. ' +
    'Post a count adjustment or a manual journal entry to align them.'
  );
}

/** "2 items with stock have no unit cost and are not valued.", or null. */
export function unvaluedNote(count: number): string | null {
  if (count <= 0) return null;
  return count === 1
    ? '1 item with stock has no unit cost and is not included in the stock value.'
    : `${count} items with stock have no unit cost and are not included in the stock value.`;
}

// ============ Errors ============

/**
 * A user-facing message for a failed inventory call. Known codes get guidance
 * on what to do; VALIDATION_ERROR and anything else show the server's message
 * (it names the field).
 */
export function inventoryErrorMessage(err: unknown, fallback: string): string {
  const server = getErrorMessage(err, fallback);
  switch (getErrorCode(err)) {
    case 'UNIT_COST_REQUIRED':
      return 'This receipt has no cost to value it at: the item has no unit cost. Enter a unit cost greater than 0 for this receipt.';
    case 'ITEM_NOT_INVENTORY_TRACKED':
      return 'Only PRODUCT items carry stock, and this item is no longer a product. Refresh the page.';
    case 'ACCOUNT_NOT_POSTABLE':
      return 'The offset account is a header account and cannot be posted to. Choose a postable account.';
    case 'INVALID_OFFSET_ACCOUNT':
      return 'The offset account cannot be 1300 Inventory itself. Choose the account the stock was paid from or is owed on, such as 2000 Accounts Payable or 1000 Cash.';
    case 'GL_ACCOUNT_MISSING':
      return `${server}. Inventory postings need 1300 Inventory - Materials (and 5000 Cost of Goods Sold for count adjustments); add it under Settings > Chart of Accounts.`;
    case 'NO_CHANGE':
      return 'The count matches the quantity on hand (it may have changed since this page loaded), so there is nothing to adjust.';
    case 'NEGATIVE_STOCK':
      return 'This would leave a negative quantity on hand (the on-hand may have changed since this page loaded). Check the count, or confirm that a negative quantity is intended and submit again.';
    case 'NOT_FOUND':
      return `${server.replace(/\.$/, '')}. It may have been deleted; refresh the page.`;
    case 'INVENTORY_ENTRY_NOT_REVERSIBLE':
      return INVENTORY_ENTRY_NOT_REVERSIBLE_MESSAGE;
    default:
      return server;
  }
}

// ============ Quantity input ============

const QUANTITY_INPUT = /^([+-]?)(\d*)(?:\.(\d{0,2}))?$/;

/**
 * Hundredths for a quantity typed by a user (at most 2 decimals, commas
 * tolerated). A sign is accepted only with `signed`. Null for blank or
 * invalid input.
 */
export function parseQuantityHundredths(input: string | null | undefined, signed = false): number | null {
  const cleaned = (input ?? '').trim().replace(/,/g, '');
  const match = QUANTITY_INPUT.exec(cleaned);
  if (!match) return null;
  const [, sign, whole, fraction = ''] = match;
  if (sign && !signed) return null;
  if (!whole && !fraction) return null;
  const value = Number(whole || '0') * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(value)) return null;
  return sign === '-' ? -value : value;
}

/** round2 half-up(|qty| x cost) in cents (qty in hundredths, cost in cents), as the backend values stock. */
export function stockValueCents(quantityHundredths: number, costCents: number): number {
  return Math.round((Math.abs(quantityHundredths) * costCents) / 100);
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

// ============ Offset accounts ============

export function offsetAccountLabel(account: Pick<types.GLAccount, 'accountNumber' | 'accountName'>): string {
  return `${account.accountNumber} · ${account.accountName}`;
}

/** Accounts a receipt may credit: postable, never 1300 Inventory, by account number. */
export function offsetAccountOptions(accounts: types.GLAccount[]): types.GLAccount[] {
  return accounts
    .filter((account) => account.isPostable && account.accountNumber !== INVENTORY_ACCOUNT_NUMBER)
    .sort((a, b) => a.accountNumber.localeCompare(b.accountNumber, undefined, { numeric: true }));
}

/** The id of 2000 Accounts Payable when it is offered, else ''. */
export function defaultOffsetAccountId(accounts: types.GLAccount[]): string {
  return offsetAccountOptions(accounts).find((account) => account.accountNumber === DEFAULT_OFFSET_ACCOUNT_NUMBER)?.id ?? '';
}

// ============ Receive form ============

export interface ReceiveForm {
  itemId: string;
  quantity: string;
  /** Optional per-unit cost of this receipt; blank = the item's cost */
  unitCost: string;
  /** Only meaningful with a unitCost; cleared when the cost is blanked */
  updateItemCost: boolean;
  offsetAccountId: string;
  adjustmentDate: string;
  memo: string;
}

export function emptyReceiveForm(itemId: string, today: string, offsetAccountId = ''): ReceiveForm {
  return { itemId, quantity: '', unitCost: '', updateItemCost: false, offsetAccountId, adjustmentDate: today, memo: '' };
}

/** Set the unit cost; a blank cost clears "update item cost" (the backend needs a cost for it). */
export function setReceiveUnitCost(form: ReceiveForm, unitCost: string): ReceiveForm {
  return { ...form, unitCost, updateItemCost: unitCost.trim() ? form.updateItemCost : false };
}

/** The cost a receipt is valued at, in cents: the entered cost, else the item's; null when neither. */
export function receiptCostCents(form: ReceiveForm, item: Pick<types.InventoryValuationItem, 'unitCost'> | undefined): number | null {
  if (form.unitCost.trim()) return parseAmountCents(form.unitCost);
  return decimalToCents(item?.unitCost ?? null);
}

/** Receipt value in cents for the preview, or null when it cannot be computed yet. */
export function receiptValueCents(form: ReceiveForm, item: Pick<types.InventoryValuationItem, 'unitCost'> | undefined): number | null {
  const quantity = parseQuantityHundredths(form.quantity);
  const cost = receiptCostCents(form, item);
  if (quantity === null || quantity <= 0 || cost === null || cost <= 0) return null;
  return stockValueCents(quantity, cost);
}

/** The first reason the receive form cannot be submitted, or null when it can (mirrors the backend rules). */
export function receiveBlocker(form: ReceiveForm, item: Pick<types.InventoryValuationItem, 'unitCost'> | undefined): string | null {
  if (!form.itemId) return 'Choose an item.';
  const quantity = parseQuantityHundredths(form.quantity);
  if (!form.quantity.trim()) return 'Enter the quantity received.';
  if (quantity === null) return 'Quantity must be a number with at most 2 decimals.';
  if (quantity <= 0) return 'Quantity must be greater than 0.';
  if (form.unitCost.trim()) {
    const cost = parseAmountCents(form.unitCost);
    if (cost === null) return 'Unit cost must be an amount like 4.25 (at most 2 decimals, not negative).';
    if (cost === 0) return 'Unit cost must be greater than 0.';
  } else if (!(decimalToCents(item?.unitCost ?? null) ?? 0)) {
    return 'This item has no unit cost. Enter the unit cost of this receipt.';
  }
  if (!form.offsetAccountId) return 'Choose the offset account.';
  if (!DATE_ONLY.test(form.adjustmentDate)) return 'Enter the date received.';
  if (form.memo.trim().length > INVENTORY_TEXT_MAX_LENGTH) return `Memo must be at most ${INVENTORY_TEXT_MAX_LENGTH} characters.`;
  return null;
}

/** POST /inventory/receive body for a submittable form (blank optional fields left out). */
export function buildReceiveRequest(form: ReceiveForm): types.ReceiveStockRequest {
  const quantity = parseQuantityHundredths(form.quantity) ?? 0;
  const cost = form.unitCost.trim() ? parseAmountCents(form.unitCost) : null;
  const memo = form.memo.trim();
  return {
    itemId: form.itemId,
    quantity: centsToDecimal(quantity),
    ...(cost !== null ? { unitCost: centsToDecimal(cost) } : {}),
    offsetAccountId: form.offsetAccountId,
    adjustmentDate: form.adjustmentDate,
    ...(memo ? { memo } : {}),
    ...(cost !== null && form.updateItemCost ? { updateItemCost: true } : {}),
  };
}

// ============ Adjust form ============

export type AdjustMode = 'count' | 'change';

export interface AdjustForm {
  itemId: string;
  mode: AdjustMode;
  /** The counted quantity (mode 'count') */
  newQuantity: string;
  /** The signed change (mode 'change') */
  quantityChange: string;
  reason: string;
  adjustmentDate: string;
  /** Confirmation for a negative result (sent only while the confirmation is shown) */
  allowNegative: boolean;
}

export function emptyAdjustForm(itemId: string, today: string): AdjustForm {
  return { itemId, mode: 'count', newQuantity: '', quantityChange: '', reason: '', adjustmentDate: today, allowNegative: false };
}

export interface AdjustPreview {
  changeHundredths: number;
  resultHundredths: number;
}

/** The change and resulting on-hand from the loaded on-hand, or null while the quantity is blank/invalid. */
export function adjustPreview(form: AdjustForm, onHand: string): AdjustPreview | null {
  const current = decimalToCents(onHand) ?? 0;
  if (form.mode === 'count') {
    const counted = parseQuantityHundredths(form.newQuantity);
    if (counted === null) return null;
    return { changeHundredths: counted - current, resultHundredths: counted };
  }
  const change = parseQuantityHundredths(form.quantityChange, true);
  if (change === null) return null;
  return { changeHundredths: change, resultHundredths: current + change };
}

/** Value the adjustment posts at the item's cost, in cents (signed: negative for a shrink); 0 without a cost. */
export function adjustValueCents(preview: AdjustPreview | null, unitCost: string | null): number {
  const cost = decimalToCents(unitCost);
  if (!preview || !cost) return 0;
  const value = stockValueCents(preview.changeHundredths, cost);
  return preview.changeHundredths < 0 ? -value : value;
}

/** True when the loaded on-hand says the result would be negative. */
export function wouldGoNegative(preview: AdjustPreview | null): boolean {
  return !!preview && preview.resultHundredths < 0;
}

/**
 * The first reason the adjust form cannot be submitted, or null. `confirmNegative`
 * is true while the negative-quantity confirmation is shown (projected negative,
 * or the server answered NEGATIVE_STOCK); it must then be ticked.
 */
export function adjustBlocker(form: AdjustForm, onHand: string, confirmNegative: boolean): string | null {
  if (!form.itemId) return 'Choose an item.';
  if (form.mode === 'count') {
    if (!form.newQuantity.trim()) return 'Enter the counted quantity.';
    if (parseQuantityHundredths(form.newQuantity) === null) return 'The count must be a number of at least 0 with at most 2 decimals.';
  } else {
    if (!form.quantityChange.trim()) return 'Enter the change, e.g. -3 or 5.';
    const change = parseQuantityHundredths(form.quantityChange, true);
    if (change === null) return 'The change must be a number with at most 2 decimals, e.g. -3 or 5.';
    if (change === 0) return 'The change must not be 0.';
  }
  const preview = adjustPreview(form, onHand);
  if (preview && preview.changeHundredths === 0) return 'The count matches the quantity on hand; nothing to adjust.';
  if (!form.reason.trim()) return 'Enter the reason for the adjustment.';
  if (form.reason.trim().length > INVENTORY_TEXT_MAX_LENGTH) return `Reason must be at most ${INVENTORY_TEXT_MAX_LENGTH} characters.`;
  if (!DATE_ONLY.test(form.adjustmentDate)) return 'Enter the adjustment date.';
  if (confirmNegative && !form.allowNegative) return 'Confirm the negative quantity on hand to continue.';
  return null;
}

/** POST /inventory/adjust body: exactly one of newQuantity / quantityChange; allowNegative only when confirmed. */
export function buildAdjustRequest(form: AdjustForm, confirmNegative: boolean): types.AdjustStockRequest {
  const quantity =
    form.mode === 'count'
      ? { newQuantity: centsToDecimal(parseQuantityHundredths(form.newQuantity) ?? 0) }
      : { quantityChange: centsToDecimal(parseQuantityHundredths(form.quantityChange, true) ?? 0) };
  return {
    itemId: form.itemId,
    ...quantity,
    reason: form.reason.trim(),
    adjustmentDate: form.adjustmentDate,
    ...(confirmNegative && form.allowNegative ? { allowNegative: true } : {}),
  };
}

// ============ History ============

/** What a history row was: a receipt, a count adjustment, an invoice issue or its cancellation. */
export function historyKind(row: Pick<types.InventoryAdjustment, 'referenceType' | 'qtyChange' | 'memo'>): string {
  if (row.referenceType === 'INVOICE') {
    return (decimalToCents(row.qtyChange) ?? 0) < 0 ? 'Invoiced' : 'Invoice cancelled';
  }
  if (row.referenceType === 'MANUAL') {
    if (row.memo?.startsWith('Stock received:')) return 'Received';
    if (row.memo?.startsWith('Count adjustment:')) return 'Count adjustment';
    return 'Manual';
  }
  return 'Other';
}

export interface HistoryLink {
  to: string;
  label: string;
}

/** INVOICE rows link to the invoice; MANUAL rows to their journal entry when one was posted. */
export function historyReferenceLink(row: Pick<types.InventoryAdjustment, 'referenceType' | 'referenceId'>): HistoryLink | null {
  if (!row.referenceId) return null;
  if (row.referenceType === 'INVOICE') return { to: `/invoices/${encodeURIComponent(row.referenceId)}`, label: 'View invoice' };
  if (row.referenceType === 'MANUAL') return { to: ledgerEntryPath(row.referenceId), label: 'View journal entry' };
  return null;
}

/** Shown for a MANUAL row with no entry. */
export const QUANTITY_ONLY_NOTE = 'Quantity only (no cost, nothing posted)';

// ============ Result ============

/** Success banner text after a receipt or adjustment. */
export function changeSummary(kind: 'receive' | 'adjust', result: types.InventoryChangeResponse): string {
  const change = formatQuantityChange(result.qtyChange);
  const onHand = formatQuantity(result.quantityOnHand);
  const head = kind === 'receive'
    ? `Received ${formatQuantity(result.qtyChange)} × ${result.itemName}.`
    : `Adjusted ${result.itemName} by ${change}.`;
  const posted = result.journalEntry
    ? ` Posted ${inventoryMoney((result.value || '0').replace(/^-/, ''))} to the ledger.`
    : kind === 'adjust'
      ? ' No journal entry was posted (the item has no unit cost, so only the quantity changed).'
      : ' No journal entry was posted (the value rounds to $0.00).';
  return `${head} On hand is now ${onHand}.${posted}`;
}
